import { escapeRegExp } from "lodash";

export enum ACTION_TYPES {
  EDIT = "Edit",
  GENERATE = "Generate",
  ADD_CONTEXT = "Add Context",
}

export const getTotalCost = (usages: any): number => {
  let totalCost = 0;
  usages.forEach((usage: any) => {
    if (usage.model === "gpt-4-32k") {
      totalCost +=
        (usage.inputTokens / 1000.0) * 0.06 +
        (usage.outputTokens / 1000.0) * 0.12;
    } else if (usage.model === "gpt-3.5-turbo-16k") {
      totalCost +=
        (usage.inputTokens / 1000.0) * 0.003 +
        (usage.outputTokens / 1000.0) * 0.004;
    } else if (usage.model === "gpt-4-turbo") {
      totalCost +=
        (usage.inputTokens / 1000.0) * 0.01 +
        (usage.outputTokens / 1000.0) * 0.03;
    } else if (usage.model === "gpt-4o") {
      totalCost +=
        (usage.inputTokens / 1000.0) * 0.005 +
        (usage.outputTokens / 1000.0) * 0.015;
    }
  });

  return totalCost;
};

export const enhancedReplace = (
  original: string,
  search: string,
  replacement: string
): string | null => {
  // First try to replace without ignoring whitespace
  const entireRegExp = new RegExp(escapeRegExp(search), "g");

  if ((original.match(entireRegExp) || []).length === 1) {
    return original.replace(entireRegExp, replacement);
  }

  // Create a stripped version of the original string without whitespace
  const strippedOriginal = original.replace(/\s+/g, "");

  const searchRegExp = new RegExp(escapeRegExp(strippedOriginal), "g");

  // If there is more than one match, return null indicating failure
  if ((original.match(searchRegExp) || []).length !== 1) {
    return null;
  }

  // Find the start position of the search string in the stripped original string
  const position = strippedOriginal.indexOf(search);

  if (position === -1) {
    return null; // if search string is not found, return null indicating failure
  }

  let j = 0; // Index for navigating through the original string
  let startPos: number | undefined = undefined;
  let endPos: number | undefined = undefined;

  // Determine the start and end positions of the substring in the original string
  for (let i = 0; i < original.length; i++) {
    if (!/\s/.test(original[i])) {
      // skip whitespace
      if (j === position && startPos === undefined) {
        startPos = i;
      }
      j++;
      if (j === position + search.length) {
        endPos = i;
        break;
      }
    }
  }

  if (startPos !== undefined && endPos !== undefined) {
    // Use the replacement to replace the substring in the original string
    return (
      original.substring(0, startPos) +
      replacement +
      original.substring(endPos + 1)
    );
  } else {
    return null;
  }
};

export async function* getIterableJsonStream(
  body: ReadableStream<Uint8Array>
): AsyncIterable<any> {
  const reader = body.getReader();
  const decoder = new TextDecoder();

  let incompleteChunk = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    try {
      const decodedChunk =
        incompleteChunk + decoder.decode(value, { stream: true });

      const jsonChunks = decodedChunk.split("\n");
      if (!decodedChunk.endsWith("\n")) {
        // The last chunk is incomplete
        // @ts-expect-error TS2322
        incompleteChunk = jsonChunks.pop();
      } else {
        incompleteChunk = "";
      }
      for (let i = 0; i < jsonChunks.length; i++) {
        const chunk = jsonChunks[i];
        if (chunk.trim() === "") continue;
        yield JSON.parse(chunk);
      }
    } catch (e) {
      console.error(e);
      console.error(decoder.decode(value, { stream: true }));
      break;
    }
  }
}

export const generateJsonStream = async (
  url: string,
  options: RequestInit & {
    queryParams?: Record<string, string>;
  }
) => {
  if (options.queryParams) {
    // Initialize URLSearchParams from the params object
    let queryParams = new URLSearchParams(options.queryParams);

    // Append query parameters to the URL
    url += `?${queryParams}`;
  }

  const { queryParams, ...fetchOptions } = options;

  const response = await fetch(url, fetchOptions);

  if (response.status !== 200) {
    console.error(response);
    throw new Error(await response.text());
  }

  if (!response.body) throw new Error("Response body does not exist");
  return getIterableJsonStream(response.body);
};
